// SPV Pool v1.0 PR 6 — Section C. Reserve fund balance vs. target with a
// gold-on-onyx progress bar, a daily-balance sparkline, and the ledger
// table. The manual-adjustment button is gated on Spv.Manage.

import { useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { Plus } from "lucide-react";
import { Card, Button, Badge } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency, formatDate, humanizeStatus } from "../../../shared/format";
import type {
  ReserveFundDto,
  ReserveFundEntryType,
} from "@/types/spvPool";
import { ManualAdjustmentDialog } from "./ManualAdjustmentDialog";

interface ReserveFundCardProps {
  poolId: string;
  reserveFund: ReserveFundDto;
}

const ENTRY_TYPE_STATUS: Record<
  ReserveFundEntryType,
  "finalized" | "error" | "info"
> = {
  TopUp: "finalized",
  Draw: "error",
  Adjustment: "info",
};

function signedCurrency(amount: number): string {
  const formatted = formatCurrency(Math.abs(amount));
  if (amount < 0) return `-${formatted}`;
  return amount > 0 ? `+${formatted}` : formatted;
}

export function ReserveFundCard({ poolId, reserveFund }: ReserveFundCardProps) {
  const { hasPermission } = useAuth();
  const canAdjust = hasPermission("Spv.Manage");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { currentBalance, targetAmount, ledger, dailyBalances } = reserveFund;
  const fillPct =
    targetAmount > 0
      ? Math.min(100, (currentBalance / targetAmount) * 100)
      : 0;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Reserve Fund</h3>
        {canAdjust && (
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} aria-hidden /> Manual Adjustment
          </Button>
        )}
      </div>

      <div className="flex items-baseline gap-space-2 mb-space-1">
        <span className="font-display font-bold text-h2 text-onyx">
          {formatCurrency(currentBalance)}
        </span>
        <span className="text-small text-gray-mid">
          of {formatCurrency(targetAmount)} target
        </span>
      </div>
      <div
        className="relative h-3 rounded-sm bg-onyx overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(fillPct)}
        aria-label="Reserve fund balance against target"
      >
        <div
          className="absolute inset-y-0 left-0 bg-gold"
          style={{ width: `${fillPct}%` }}
        />
      </div>

      {dailyBalances.length > 0 && (
        <div className="mt-space-3">
          <div className="text-label font-display uppercase tracking-label text-gold-shadow mb-space-1">
            Daily balance
          </div>
          <ResponsiveContainer width="100%" height={80}>
            <AreaChart data={dailyBalances}>
              <Area
                type="monotone"
                dataKey="balance"
                stroke="var(--gold-shadow)"
                fill="var(--gold)"
                fillOpacity={0.3}
              />
              <Tooltip
                formatter={(v: number) => formatCurrency(v)}
                labelFormatter={(l: string) => formatDate(l)}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-space-3">
        <div className="text-label font-display uppercase tracking-label text-gold-shadow mb-space-1">
          Ledger
        </div>
        {ledger.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No reserve fund ledger entries for this period.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Occurred At</TH>
                <TH>Entry Type</TH>
                <TH>Source</TH>
                <TH>Amount</TH>
                <TH>Memo</TH>
                <TH>Related Distribution</TH>
              </TR>
            </THead>
            <TBody>
              {ledger.map((e) => (
                <TR key={e.id}>
                  <TD>{formatDate(e.occurredAt)}</TD>
                  <TD>
                    <Badge status={ENTRY_TYPE_STATUS[e.entryType]}>
                      {humanizeStatus(e.entryType)}
                    </Badge>
                  </TD>
                  <TD>
                    <Badge status="archived">
                      {humanizeStatus(e.source)}
                    </Badge>
                  </TD>
                  <TD>{signedCurrency(e.amount)}</TD>
                  <TD>{e.memo ?? "—"}</TD>
                  <TD>
                    {e.relatedWaterfallDistributionId ? (
                      <span className="font-mono text-caption">
                        {e.relatedWaterfallDistributionId.slice(0, 8)}
                      </span>
                    ) : (
                      <span className="text-gray-mid">—</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <ManualAdjustmentDialog
        poolId={poolId}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Card>
  );
}
